import { type FC, useEffect, useState } from 'react';
import { Flex, Form } from 'antd';
import { DatePicker, Modal } from '@g2u/ui-kit';
import { format, isBefore, isSameDay, startOfDay } from 'date-fns';

import { validateFormSilently } from 'helpers';
import { inclusiveDayCount } from 'pages/technician/edit/helpers/availability';
import styles from './technician-away-status-modal.module.scss';

type Props = {
  isOpen: boolean;
  initialValues?: AwayStatusModal.InitialValues;
  isLoading: boolean;
  onSubmit: (values: AwayStatusModal.Payload) => void;
  onClose: () => void;
};

type FormFields = {
  startDate?: Date;
  endDate?: Date;
};

const DATE_FORMAT = 'd MMM yyyy';

const isBeforeToday = (date: Date | null) => !!date && isBefore(date, startOfDay(new Date()));

const toApiDate = (date?: Date) => (date ? format(date, 'yyyy-MM-dd') : '');

export const AwayStatusModal: FC<Props> = ({ isOpen, initialValues, isLoading, onSubmit, onClose }) => {
  const [form] = Form.useForm<FormFields>();
  const [isFormValid, setIsFormValid] = useState(false);
  const startDate = Form.useWatch('startDate', form);
  const endDate = Form.useWatch('endDate', form);
  const isEditing = !!initialValues;
  const isChanged =
    !isEditing ||
    toApiDate(startDate) !== toApiDate(initialValues.startDate) ||
    toApiDate(endDate) !== toApiDate(initialValues.endDate);

  useEffect(() => {
    if (isOpen) {
      form.resetFields();
      setIsFormValid(false);

      if (initialValues) {
        validateFormSilently(form, setIsFormValid);
      }
    }
  }, [isOpen, initialValues, form]);

  const handleFormChange = (changedValues: FormFields) => {
    if ('startDate' in changedValues && form.isFieldTouched('endDate')) {
      form.validateFields(['endDate']).catch(() => {});
    }
    validateFormSilently(form, setIsFormValid);
  };

  const handleFinish = (values: FormFields) => {
    onSubmit({
      start_date: toApiDate(values.startDate),
      end_date: toApiDate(values.endDate),
    });
  };

  return (
    <Modal.$
      dataTestId="technician-away-status-modal"
      open={isOpen}
      title={isEditing ? 'Edit Away Status' : 'Add Away Status'}
      width={924}
      okText={isEditing ? 'Save' : 'Add'}
      cancelText="Cancel"
      onOk={form.submit}
      onCancel={onClose}
      cancelButtonProps={{ disabled: isLoading }}
      okButtonProps={{ disabled: !isFormValid || !isChanged || isLoading, loading: isLoading, style: { width: 120 } }}
    >
      <Form
        form={form}
        initialValues={initialValues}
        layout="vertical"
        preserve={false}
        disabled={isLoading}
        onValuesChange={handleFormChange}
        onFinish={handleFinish}
      >
        <Flex gap={16}>
          <DatePicker.$
            label="Starting Date"
            name="startDate"
            colon
            required
            rules={[{ required: true, message: 'Starting Date is required.' }]}
            format={DATE_FORMAT}
            disabledDate={(date) => isBeforeToday(date) && !(date && initialValues && isSameDay(date, initialValues.startDate))}
            className={styles.awayStatusModal__date}
          />
          <DatePicker.$
            label="Ending Date"
            name="endDate"
            colon
            required
            rules={[
              { required: true, message: 'Ending Date is required.' },
              {
                validator: (_, value?: Date) =>
                  value && form.getFieldValue('startDate') && isBefore(startOfDay(value), startOfDay(form.getFieldValue('startDate')))
                    ? Promise.reject(new Error('Ending Date can not be before the Starting Date.'))
                    : Promise.resolve(),
              },
            ]}
            format={DATE_FORMAT}
            disabledDate={(date) => isBeforeToday(date) || (!!date && !!startDate && isBefore(startOfDay(date), startOfDay(startDate)))}
            className={styles.awayStatusModal__date}
          />
          <Form.Item label="Days" colon>
            <span data-testid="away-status-days">{inclusiveDayCount(startDate, endDate)}</span>
          </Form.Item>
        </Flex>
      </Form>
    </Modal.$>
  );
};

export namespace AwayStatusModal {
  export type InitialValues = {
    startDate: Date;
    endDate?: Date;
  };

  export type Payload = {
    start_date: string;
    end_date: string;
  };
}
